import newsletterRepository from '@/api/newsletterRepository';
import useHashParams from '@/composables/general/useHashParams';
import { domainStore } from '@/store/domain-store';
import { NewsletterInterface, NewsletterSubscription } from '@/models/newsletter.model';
import { RouteLocationNormalized } from 'vue-router';
import { PersistentStore } from './main';

interface NewsletterStoreModel extends Object {
  newsletters: Array<NewsletterInterface>;
  activeTab: number | null;
  singleNewsletter: NewsletterInterface | null;
  subscriptions: Array<NewsletterSubscription>;
}

class NewsletterStore extends PersistentStore<NewsletterStoreModel> {
  protected data(): NewsletterStoreModel {
    return {
      newsletters: [],
      activeTab: null,
      singleNewsletter: null,
      subscriptions: [],
    };
  }

  getNewsletters() {
    if (this.state.newsletters.length > 1) {
      return this.state.newsletters;
    }
    if (this.state.newsletters.length === 1) {
      this.state.singleNewsletter === this.state.newsletters[0];
      return this.state.newsletters;
    }

    return newsletterRepository
      .get()
      .then((response: Array<NewsletterInterface>) => {
        if (response.length === 1) {
          this.state.singleNewsletter = response[0];
          this.state.newsletters = response;
          return response;
        }
        this.state.newsletters = response;
        return response;
      })
      .catch((error: any) => {
        console.error(error);
      });
  }
  getSingleNewsletter(newsletterid: number) {
    if (this.state.singleNewsletter) {
      return this.state.singleNewsletter;
    }
    const fetchedNewsletter = this.state.newsletters.find(
      (newsletter: NewsletterInterface) => newsletter.id === newsletterid
    );
    if (fetchedNewsletter) {
      this.state.singleNewsletter = fetchedNewsletter;
      return this.state.singleNewsletter;
    }
    return newsletterRepository
      .getSingleNewsletter(newsletterid)
      .then((response: NewsletterInterface) => {
        this.state.singleNewsletter = response;
        return response;
      })
      .catch((error: any) => {
        console.error(error);
      });
  }
  // getFetchedSubscription(newsletterid: number, email: string) {
  //   return this.state.subscriptions.find((subscription: NewsletterSubscription) => {
  //     return subscription.email == email && subscription.id == newsletterid;
  //   });
  // }
  getSubscription(newsletterid: number) {
    return new Promise((resolve, reject) => {
      newsletterRepository
        .getSubscription(newsletterid)
        .then((response: any) => {
          if (response) {
            if (response.subscription.verified === 0) {
              resolve('Sie haben diesen Newsletter bereits abonniert, müssen ihn aber noch per e-Mail verifizieren.');
            }
            if (response.subscription.verified === 1) {
              resolve('Sie haben diesen Newsletter bereits abonniert.');
            }
          }
        })
        .catch(() => {
          reject('Kein Abonnement zu Newsletter ' + newsletterid + ' gefunden.');
        });
    });
  }

  verifyToken(newsletterid: number, token: string) {
    return new Promise((resolve, reject) => {
      newsletterRepository
        .verifyToken(newsletterid, token)
        .then((response: NewsletterSubscription) => {
          if (response.subscription.verified === 0) {
            resolve('Verifizierung fehlgeschlagen!');
          }
          if (response.subscription.verified === 1) {
            resolve('Sie haben sich erfolgreich verifiziert!');
          }
        })
        .catch((error: any) => {
          console.error(error);
          reject(
            `Bei der Verifikation Ihrer Anmeldung für den Newsletter ${newsletterid} ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.`
          );
        });
    });
  }

  subscribe(newsletterid: number, email: string, captchaId = 0, captchaAnswer = '') {
    return new Promise((resolve, reject) => {
      newsletterRepository
        .subscribe(newsletterid, email, captchaId, captchaAnswer)
        .then((response: NewsletterSubscription) => {
          if (response.subscription.verified === 0) {
            resolve(
              'Sie erhalten nun eine E-Mail mit einem Aktivierungslink, um Ihre E-Mail-Adresse zu bestätigen. Nachdem Sie auf den Link in dieser E-Mail geklickt haben, erhalten Sie den gewünschten Newsletter. Sollten Sie keine E-Mail erhalten haben, prüfen Sie bitte Ihren Spam-Ordner. Bitte beachten Sie: Wenn Sie die Anmeldung nicht innerhalb von 14 Tagen bestätigen, wird Ihr Eintrag aus Datenschutzgründen automatisch gelöscht.'
            );
          }
          if (response.subscription.verified === 1) {
            resolve('Sie haben den Newsletter erfolgreich abonniert.');
          }
        })
        .catch((error: any) => {
          const errData = error.response.data;
          if (errData.message.includes('Sicherheitsabfrage')) {
            reject({
              type: 'captcha',
              message: errData.message,
            });
          } else {
            reject({
              type: 'other',
              message: `Bei der Anmeldung für den Newsletter ${newsletterid} ist ein Fehler aufgetreten. Bitte melden Sie sich erneut für den Newsletter an.`,
            });
          }
        });
    });
  }

  unsubscribe(newsletterid: number, token: string) {
    return new Promise((resolve, reject) => {
      newsletterRepository
        .unsubscribe(Number(newsletterid), String(token))
        .then((response: any) => {
          if (response) {
            resolve('Sie haben sich erfolgreich vom Newsletter abgemeldet.');
          } else {
            resolve('Keine Registrierung für diesen Newsletter gefunden oder der Link ist fehlerhaft.');
          }
        })
        .catch((error: any) => {
          console.error(error);
          reject(`Sie haben sich bereits vom Newsletter ${newsletterid} abgemeldet.`);
        });
    });
  }

  changeNewsletterQuery(activeTab: number, route: RouteLocationNormalized) {
    const { setHashParams } = useHashParams(route);
    if (!this.state.newsletters || this.state.newsletters.length === 0) {
      return;
    }
    this.state.newsletters.forEach((newsletter: NewsletterInterface, index: number) => {
      if (activeTab === index) {
        setHashParams({ nl_newsletterid: Number(newsletter.id) });
      }
    });
  }

  getActiveTab(newsletterid: number) {
    if (!this.state.newsletters || this.state.newsletters.length === 0) {
      return;
    }
    let activeTab = -1;
    this.state.newsletters.forEach((newsletter: NewsletterInterface, index: number) => {
      if (newsletterid && newsletter.id === newsletterid) {
        activeTab = index;
      }
    });
    return activeTab;
  }
}
export const newsletterStore = new NewsletterStore('NEWSLETTER_STORE');
